import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { AccountSelector } from "../settings/account-selector.js";
import { useAccounts } from "../settings/queries.js";
import { InvalidUsdAmountError } from "../../shared/lib/usd.js";
import { Button } from "../../shared/ui/button.js";
import { ResourceState } from "../../shared/ui/resource-state.js";
import { botMutation, type BotFormValues, valuesFromConfiguration } from "./bot-mutation.js";
import { DecisionGraphEditor } from "./decision-graph-editor.js";
import {
  defaultBotConfiguration,
  isMonthlyStrategy,
  type BotRevisionView,
  type BotView,
} from "./models.js";
import { monthlyTrendTemplates, monthlyTrendTemplateValues } from "./monthly-trend-template.js";
import { ProblemNotice } from "./problem-notice.js";
import { useBot, useBotRevisions, useCreateBot, useUpdateBot } from "./queries.js";

interface BotFormProps {
  readonly bot?: BotView;
  readonly revision?: BotRevisionView;
}

export function BotForm({ bot, revision }: BotFormProps) {
  const navigate = useNavigate();
  const accounts = useAccounts();
  const create = useCreateBot();
  const update = useUpdateBot(bot?.id ?? "");
  const mutation = bot === undefined ? create : update;
  const form = useForm<BotFormValues>({
    defaultValues: valuesFromConfiguration(
      revision ?? defaultBotConfiguration,
      bot?.name,
      bot?.liveBrokerAccountId,
    ),
  });
  const liveBrokerAccountId = useWatch({ control: form.control, name: "liveBrokerAccountId" });
  const strategyKind = useWatch({ control: form.control, name: "strategyKind" });
  const parametersJson = useWatch({ control: form.control, name: "parametersJson" });
  const defaultOrderType = useWatch({ control: form.control, name: "defaultOrderType" });
  const symbol = useWatch({ control: form.control, name: "symbol" });
  const strategyParameters = useMemo((): unknown => {
    try {
      return JSON.parse(parametersJson);
    } catch {
      return null;
    }
  }, [parametersJson]);

  useEffect(() => {
    form.reset(
      valuesFromConfiguration(
        revision ?? defaultBotConfiguration,
        bot?.name,
        bot?.liveBrokerAccountId,
      ),
    );
  }, [bot, form, revision]);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors(["maxPositionNotionalUsd", "parametersJson"]);
    let body;
    try {
      body = botMutation(values, bot === undefined);
    } catch (error) {
      if (error instanceof InvalidUsdAmountError) {
        form.setError("maxPositionNotionalUsd", { message: error.message });
        return;
      }
      form.setError("parametersJson", { message: "Strategy parameters must be valid JSON." });
      return;
    }
    if (bot === undefined) await create.mutateAsync(body);
    else await update.mutateAsync({ body, expectedVersion: bot.version });
    void navigate("/bots");
  });

  return (
    <form className="form-stack" onSubmit={(event) => void submit(event)} noValidate>
      {bot === undefined ? (
        <div className="field-stack">
          <label htmlFor="local-bot-template">Local research template</label>
          <select
            id="local-bot-template"
            defaultValue=""
            onChange={(event) => {
              if (event.target.value !== "")
                form.reset(monthlyTrendTemplateValues(event.target.value));
            }}
          >
            <option value="">Choose a template to replace this form</option>
            {monthlyTrendTemplates.map((template) => (
              <option key={template.configuration.symbol} value={template.configuration.symbol}>
                {template.name}
              </option>
            ))}
          </select>
          <span className="field-hint">
            Selecting a template only fills this form. Nothing is saved until Create draft bot.
          </span>
        </div>
      ) : null}
      {isMonthlyStrategy({
        kind: strategyKind,
        version: "1",
        parameters:
          strategyParameters !== null && typeof strategyParameters === "object"
            ? (strategyParameters as Record<string, unknown>)
            : {},
      }) ? (
        <p className="section-description" role="note">
          Backtest-only multi-day strategy: month-end close versus its monthly SMA (preset: 10
          months), with orders at the next session open. The $10,000 entry budget includes
          commission. No broker account is attached by the preset. Historical parity is pending;
          this is not a verified profitable candidate.
        </p>
      ) : null}
      <div className="field-grid">
        <div className="field-stack">
          <label htmlFor="bot-name">Bot name</label>
          <input id="bot-name" {...form.register("name", { required: true })} required />
        </div>
        <div className="field-stack">
          <label htmlFor="bot-symbol">Stock symbol</label>
          <input
            id="bot-symbol"
            autoCapitalize="characters"
            {...form.register("symbol", { required: true })}
            required
          />
          <span className="field-hint">TradingEngine validates the canonical Stock symbol.</span>
        </div>
      </div>

      <fieldset className="form-section">
        <legend>Strategy</legend>
        <p className="section-description">
          Strategy and indicator fields are interpreted and validated only by TradingEngine.
        </p>
        <div className="field-grid">
          <div className="field-stack">
            <label htmlFor="strategy-kind">Strategy kind</label>
            <input
              id="strategy-kind"
              {...form.register("strategyKind", { required: true })}
              required
            />
          </div>
          <div className="field-stack">
            <label htmlFor="strategy-version">Strategy version</label>
            <input
              id="strategy-version"
              {...form.register("strategyVersion", { required: true })}
              required
            />
          </div>
        </div>
        {strategyKind === "decision-graph" ? (
          <DecisionGraphEditor
            botName={bot?.name ?? "New Bot"}
            botVersion={bot?.version ?? 1}
            onChange={(parameters) =>
              form.setValue("parametersJson", JSON.stringify(parameters, null, 2), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            orderType={defaultOrderType}
            parameters={strategyParameters}
            revision={revision?.revision ?? 1}
            symbol={symbol}
          />
        ) : null}
        <details className="advanced-json" open={strategyKind !== "decision-graph"}>
          <summary>Advanced strategy JSON</summary>
          <div className="field-stack">
            <label htmlFor="strategy-parameters">Parameters (JSON)</label>
            <textarea
              id="strategy-parameters"
              rows={12}
              spellCheck={false}
              {...form.register("parametersJson", { required: true })}
              required
            />
            {form.formState.errors.parametersJson === undefined ? null : (
              <span className="field-error">{form.formState.errors.parametersJson.message}</span>
            )}
          </div>
        </details>
      </fieldset>

      <fieldset className="form-section">
        <legend>Position sizing and risk</legend>
        <div className="field-grid">
          <div className="field-stack">
            <label htmlFor="sizing-kind">Sizing method</label>
            <select id="sizing-kind" {...form.register("positionSizingKind")}>
              <option value="fixed-quantity">Fixed quantity</option>
              <option value="fixed-notional">Fixed USD notional</option>
              <option value="percent-of-equity">Percent of equity</option>
            </select>
          </div>
          <div className="field-stack">
            <label htmlFor="sizing-value">Sizing value</label>
            <input
              id="sizing-value"
              inputMode="decimal"
              {...form.register("positionSizingValue", { required: true })}
              required
            />
          </div>
          <div className="field-stack">
            <label htmlFor="max-quantity">
              Maximum position quantity <span>(optional)</span>
            </label>
            <input
              id="max-quantity"
              inputMode="decimal"
              {...form.register("maxPositionQuantity")}
            />
          </div>
          <div className="field-stack">
            <label htmlFor="max-notional">
              Maximum position notional (USD) <span>(optional)</span>
            </label>
            <input
              id="max-notional"
              inputMode="decimal"
              placeholder="6000"
              {...form.register("maxPositionNotionalUsd")}
            />
            <span className="field-hint">Enter dollars, for example 6000 for $6,000.</span>
            {form.formState.errors.maxPositionNotionalUsd === undefined ? null : (
              <span className="field-error">
                {form.formState.errors.maxPositionNotionalUsd.message}
              </span>
            )}
          </div>
        </div>
        <label className="check-field">
          <input type="checkbox" {...form.register("allowShort")} />
          <span>
            <strong>Allow short positions</strong>
            <small>TradingEngine risk controls still apply.</small>
          </span>
        </label>
      </fieldset>

      <fieldset className="form-section">
        <legend>Schedule and execution</legend>
        <div className="field-grid">
          <div className="field-stack">
            <label htmlFor="exchange">Primary exchange calendar</label>
            <select id="exchange" {...form.register("exchange")}>
              <option value="XNYS">NYSE (XNYS)</option>
              <option value="XNAS">Nasdaq (XNAS)</option>
            </select>
          </div>
          <div className="field-stack">
            <label htmlFor="order-type">Default order type</label>
            <select id="order-type" {...form.register("defaultOrderType")}>
              <option value="market">Market</option>
              <option value="limit">Limit</option>
            </select>
          </div>
          <div className="field-stack">
            <label htmlFor="time-in-force">Time in force</label>
            <select id="time-in-force" {...form.register("timeInForce")}>
              <option value="day">Day</option>
              <option value="gtc">Good until cancelled</option>
            </select>
          </div>
          <AccountSelector
            accounts={accounts.data ?? []}
            id="live-account"
            onChange={(value) => form.setValue("liveBrokerAccountId", value, { shouldDirty: true })}
            value={liveBrokerAccountId}
          />
        </div>
        <label className="check-field">
          <input type="checkbox" {...form.register("regularSessionOnly")} />
          <span>
            <strong>Regular session only</strong>
            <small>Schedule timezone is fixed to America/New_York.</small>
          </span>
        </label>
      </fieldset>

      {mutation.error === null ? null : <ProblemNotice error={mutation.error} />}
      <div className="button-row">
        <Button disabled={mutation.isPending} type="submit">
          {mutation.isPending
            ? "Saving…"
            : bot === undefined
              ? "Create draft bot"
              : "Save new revision"}
        </Button>
        <Link className="button button--quiet" to={bot === undefined ? "/bots" : `/bots/${bot.id}`}>
          Cancel
        </Link>
        {mutation.error instanceof Error &&
        "status" in mutation.error &&
        mutation.error.status === 409 ? (
          <Button onClick={() => window.location.reload()} tone="quiet" type="button">
            Reload latest bot
          </Button>
        ) : null}
      </div>
    </form>
  );
}

export function BotEditorPage({ botId }: { readonly botId?: string }) {
  if (botId === undefined) {
    return (
      <section className="page-stack" aria-labelledby="bot-editor-title">
        <header className="page-header">
          <p className="eyebrow">One symbol, one strategy</p>
          <h1 id="bot-editor-title">Create bot</h1>
          <p>Create a versioned stock Bot without templates or Bot Groups.</p>
        </header>
        <div className="content-card">
          <BotForm />
        </div>
      </section>
    );
  }
  return <LoadedBotEditor botId={botId} />;
}

function LoadedBotEditor({ botId }: { readonly botId: string }) {
  const bot = useBot(botId);
  const revisions = useBotRevisions(botId);
  if (bot.isLoading || revisions.isLoading)
    return (
      <ResourceState
        kind="loading"
        title="Loading bot"
        description="Loading the current version and active revision."
      />
    );
  if (bot.error !== null || revisions.error !== null)
    return (
      <ResourceState
        kind="error"
        title="Bot could not be loaded"
        description="TradingEngine did not return this bot and its revisions."
      />
    );
  if (bot.data === undefined)
    return (
      <ResourceState
        kind="not-found"
        title="Bot not found"
        description="The bot may have been archived or removed."
      />
    );
  const revision =
    revisions.data?.find((item) => item.id === bot.data.activeRevisionId) ?? revisions.data?.[0];
  if (revision === undefined)
    return (
      <ResourceState
        kind="degraded"
        title="Active revision unavailable"
        description="The bot exists, but its configuration revision could not be loaded."
      />
    );
  if (["archived", "backtesting", "starting", "live", "stopping"].includes(bot.data.state))
    return (
      <ResourceState
        kind="forbidden"
        title="Bot is not editable"
        description={`A bot in ${bot.data.state} state cannot be edited.`}
        action={
          <Link className="button button--quiet" to={`/bots/${bot.data.id}`}>
            Return to bot
          </Link>
        }
      />
    );
  return (
    <section className="page-stack" aria-labelledby="bot-editor-title">
      <header className="page-header">
        <p className="eyebrow">Revision {revision.revision}</p>
        <h1 id="bot-editor-title">Edit {bot.data.name}</h1>
        <p>Saving creates a new immutable configuration revision.</p>
      </header>
      <div className="content-card">
        <BotForm bot={bot.data} revision={revision} />
      </div>
    </section>
  );
}
